import { FunctionComponent, useEffect, useRef, useState } from "react";
import {
  Box,
  Button as MuiButton,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Switch,
  Grid,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  Paper,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import { LinearProgress } from "@mui/material";
import { Text } from "components/atoms/text";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { Result, Search, SearchPost } from "core/types/search.types";
import {
  searchActions,
  searchAsync,
  searchSelectors,
} from "core/store/modules/search";
//TODO: Add Empty State
import { EmptyState } from "components/atoms/empty-state";
import { SearchTable } from "components/organisms/search-table";
import { favouriteUrlAsync } from "core/store/modules/favourite-url";
import { AddFavouriteUploadParams } from "core/types/favourite-url.types";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material";
import { customOrange } from "core/theme/variants";
import {
  practiceActions,
  practiceAsync,
  practiceSelectors,
} from "core/store/modules/practice";

const Button = styled(MuiButton)(spacing);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Modalita' Social (additiva): chiavi ESATTE attese dal backend/SFM
// (search_type='social' + platforms + include_comments).
const SOCIAL_PLATFORMS = [
  "facebook",
  "instagram",
  "x",
  "tiktok",
  "reddit",
  "youtube",
] as const;

interface OwnProps {
  researchFields?: any;
  onClose: () => void;
}
interface StateProps {
  search?: Search;
  // dettaglio aggiornato dal polling progressivo (results parziali + status)
  resultDetail?: Search;
  isSearchLoading: boolean;
  isPracticeLoading: boolean;
  errorPractice?: string;
  successPractice?: string;
}
interface DispatchProps {
  createNewSearch: (payload: SearchPost) => void;
  resetSearch: () => void;
  // refetch silenzioso del dettaglio (senza spinner) per il polling progressivo
  pollDetail: (id: string) => void;
  resetDetail: () => void;
  createPractice: (urls: Result[]) => void;
  resetErrorPractice: () => void;
  resetSuccessPractice: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const ResearchFieldsContainer: FunctionComponent<Props> = ({
  onClose,
  search,
  resultDetail,
  isSearchLoading,
  isPracticeLoading,
  createNewSearch,
  resetSearch,
  pollDetail,
  resetDetail,
  createPractice,
  errorPractice,
  successPractice,
  resetErrorPractice,
  resetSuccessPractice,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [newSearch, setNewSearch] = useState<SearchPost>();
  const [searchType, setSearchType] = useState<
    "generic" | "reputation" | "thematic" | "social"
  >("generic");
  // --- Modalita' Social (additiva): piattaforme selezionate + toggle commenti.
  // Usati solo quando searchType === "social"; non toccano le altre modalita'.
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [includeComments, setIncludeComments] = useState(false);
  const [sortedBy, setSortedBy] = useState<"relevance" | "date" | "rating">(
    "relevance"
  );
  const [country, setCountry] = useState("general");
  const [language, setLanguage] = useState("general");
  const [name, setName] = useState("");
  const [blacklistParams, setBlackListParams] = useState<string[]>([]);
  const [param, setParam] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // --- Ricerca progressiva -------------------------------------------------
  // Il dettaglio aggiornato dal polling (resultDetail) ha priorita' sui dati
  // iniziali di newSearch (search), che servono solo a ricavare l'id.
  const liveSearch = resultDetail ?? search;
  const liveResults: Result[] = liveSearch?.results ?? [];
  const liveStatus = liveSearch?.status;
  // consideriamo conclusa la ricerca solo quando il backend segnala 'done' e
  // non c'e' piu' la POST iniziale in corso (isSearchLoading)
  const isDone = liveStatus === "done" && !isSearchLoading;
  // id da pollare: lo restituisce newSearch (salvato nello store come search.id)
  const searchId =
    search?.id !== undefined && search?.id !== null ? String(search.id) : "";
  // teniamo un riferimento all'interval per poterlo pulire ovunque
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // Avvia/ferma il polling progressivo: parte quando siamo nello step risultati
  // e abbiamo un id, si ferma quando status === 'done' o allo smontaggio.
  useEffect(() => {
    // niente da fare se non siamo nello step risultati o non c'e' un id
    if (activeStep !== 1 || !searchId) {
      stopPolling();
      return;
    }
    // ricerca gia' conclusa: nessun polling necessario
    if (isDone) {
      stopPolling();
      return;
    }
    // primo fetch immediato cosi' i risultati parziali arrivano subito
    pollDetail(searchId);
    pollIntervalRef.current = setInterval(() => {
      pollDetail(searchId);
    }, 3500);

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, searchId, isDone]);

  // pulizia di sicurezza allo smontaggio del componente
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const contryList = ["general", "italy", "usa", "uk", "france", "spain"];

  const languageList = ["general", "italian", "english", "french", "spanish"];

  useEffect(() => {
    const newSearchValue: SearchPost = {
      searchType: searchType,
      sortedBy: sortedBy,
      country: country,
      language: language,
      fullName: name,
      params: blacklistParams,
      // Modalita' Social (additiva): valorizzati solo per searchType === "social"
      platforms: searchType === "social" ? platforms : undefined,
      include_comments: searchType === "social" ? includeComments : undefined,
    };
    setNewSearch({ ...newSearchValue });
  }, [
    searchType,
    sortedBy,
    country,
    language,
    name,
    blacklistParams,
    platforms,
    includeComments,
  ]);

  const resetFields = () => {
    setNewSearch(undefined);
    stopPolling();
    resetDetail();
    // azzera anche lo store.search: altrimenti il suo id resta "stale" e al
    // riaperture/nuova ricerca si farebbe un polling transitorio sull'id sbagliato.
    resetSearch();
  };

  const handleSubmit = () => {
    setActiveStep(1);
    // azzera dettaglio E store.search precedenti prima di partire, così il
    // contatore e la tabella ripartono puliti e l'id da pollare non è stale
    // finché la nuova POST non risponde col nuovo id.
    resetDetail();
    resetSearch();
    if (newSearch) createNewSearch(newSearch);
  };

  // Fix: modalita' Social senza piattaforme selezionate degrada silenziosamente
  // a ricerca tematica lato server. Blocchiamo il submit finche' non ce n'e' almeno una.
  const isSocialWithoutPlatforms =
    searchType === "social" && platforms.length === 0;

  // Stessa formula di `search_credit_cost` nel backend (controllers/Search.py): in Modalita'
  // Social ogni piattaforma e' una ricerca a se' e costa 1 credito. Duplicare la formula qui
  // e' voluto — serve a mostrare il prezzo mentre l'utente spunta le caselle, senza una
  // chiamata di rete a ogni clic. Se il listino cambia, vanno aggiornati entrambi: per questo
  // il backend espone anche GET /searches/cost, che resta la fonte autorevole.
  const costoCrediti =
    searchType === "social" && platforms.length > 0 ? platforms.length : 1;

  const handleGeneratePractice = (urls: Result[]) => {
    createPractice(urls);
  };

  const handleAddParam = () => {
    if (param.length > 0) {
      setBlackListParams([...blacklistParams, param]);
      setParam("");
    }
  };

  const removeParam = (param: string) => {
    const updatedParams = blacklistParams.filter((p) => p !== param);
    setBlackListParams(updatedParams);
  };

  useEffect(() => {
    if (successPractice !== "" && successPractice !== undefined) {
      setOpenSnackbar(true);
    }
  }, [successPractice]);

  useEffect(() => {
    if (errorPractice) {
      setOpenSnackbar(true);
    }
  }, [errorPractice]);

  return (
    <>
      <IconButton
        onClick={() => {
          onClose();
          resetFields();
        }}
        sx={{
          position: "absolute",
          right: 5,
          top: 5,
        }}
      >
        <CloseRoundedIcon color="info" />
      </IconButton>
      {/* {isReportLoading && <LoadingOverlay open={isReportLoading} />} */}
      <Grid container spacing={3} height={"100%"}>
        <Grid
          item
          width={"100%"}
          display="flex"
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Text variant="h3" fontWeight={"900"} mt={"10px"}>
            {activeStep === 0
              ? t("new-search.title")
              : t("new-search.results.title")}
          </Text>
          <Text variant="body1">
            {t("new-search.help")}
            <Link
              onClick={() =>
                window.open("https://t.me/codertd", "_blank", "noreferrer")
              }
              sx={{ marginLeft: "5px" }}
            >
              {t("new-search.contact")}
            </Link>
          </Text>
        </Grid>
        <Grid container item spacing={4} sx={{ marginTop: "10px" }}>
          {activeStep === 0 && (
            <>
              <Grid item xs={12} sm={4}>
                <Text
                  variant="body1"
                  sx={{
                    marginBottom: "20px",
                  }}
                >
                  {t("new-search.description1")}
                </Text>
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
                display={"flex"}
                justifyContent={"flex-start"}
                flexDirection={"column"}
              >
                <FormControl
                  fullWidth
                  variant="standard"
                  sx={{ marginBottom: "26px" }}
                >
                  <InputLabel
                    id="type-label"
                    sx={{ fontSize: "14px !important", fontWeight: "700" }}
                  >
                    {t("new-search.fields.search_type")}
                  </InputLabel>
                  <Select
                    labelId="type-label"
                    id="type-label"
                    value={searchType}
                    onChange={(event) =>
                      setSearchType(
                        event.target.value as
                          | "generic"
                          | "reputation"
                          | "thematic"
                          | "social"
                      )
                    }
                    MenuProps={MenuProps}
                    color="secondary"
                  >
                    {["generic", "reputation", "thematic", "social"].map(
                      (type) => {
                        return (
                          <MenuItem key={type} value={type}>
                            <ListItemText primary={t("type." + type)} />
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>

                {searchType === "social" && (
                  <FormControl
                    fullWidth
                    component="fieldset"
                    variant="standard"
                    sx={{ marginBottom: "26px" }}
                  >
                    <Text
                      fontSize={"14px"}
                      fontWeight={"700"}
                      sx={{ marginBottom: "8px" }}
                    >
                      {t("new-search.fields.platforms")}
                    </Text>
                    <FormGroup row>
                      {SOCIAL_PLATFORMS.map((platform) => (
                        <FormControlLabel
                          key={platform}
                          control={
                            <Checkbox
                              checked={platforms.includes(platform)}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setPlatforms([...platforms, platform]);
                                } else {
                                  setPlatforms(
                                    platforms.filter((p) => p !== platform)
                                  );
                                }
                              }}
                              color="secondary"
                            />
                          }
                          label={t(`new-search.platforms.${platform}`)}
                        />
                      ))}
                    </FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeComments}
                          onChange={(event) =>
                            setIncludeComments(event.target.checked)
                          }
                          color="secondary"
                        />
                      }
                      label={t("new-search.fields.include_comments")}
                    />
                    <Text
                      fontSize={"12px"}
                      fontWeight={"400"}
                      sx={{ color: "#5A6478", marginTop: "4px" }}
                    >
                      {t("new-search.social_note")}
                    </Text>
                    {platforms.length === 0 && (
                      <Text
                        fontSize={"12px"}
                        fontWeight={"600"}
                        sx={{ color: theme.palette.error.main, marginTop: "8px" }}
                      >
                        {t("new-search.platforms_required")}
                      </Text>
                    )}
                  </FormControl>
                )}

                {/* COSTO, sempre visibile e aggiornato mentre si spuntano le piattaforme.
                    In Modalita' Social ogni piattaforma e' una ricerca a se' (query separate,
                    quota separata) e costa 1 credito: senza dirlo qui, l'utente ne seleziona
                    sei e si ritrova addebitato sei volte senza averlo capito. Il numero e'
                    la stessa formula che il backend usa per scalare (`search_credit_cost`). */}
                <Box
                  sx={{
                    marginBottom: "26px",
                    padding: "12px 14px",
                    borderRadius: "8px",
                    border: "1px solid #E3E7EF",
                    backgroundColor: "#F7F9FC",
                  }}
                >
                  <Text fontSize={"14px"} fontWeight={"700"}>
                    {t("new-search.cost.title", { count: costoCrediti })}
                  </Text>
                  <Text
                    fontSize={"12px"}
                    fontWeight={"400"}
                    sx={{ color: "#5A6478", marginTop: "4px" }}
                  >
                    {searchType === "social"
                      ? t("new-search.cost.social_rule", {
                          count: platforms.length,
                        })
                      : t("new-search.cost.standard_rule")}
                  </Text>
                  <Text
                    fontSize={"12px"}
                    fontWeight={"400"}
                    sx={{ color: "#5A6478", marginTop: "4px" }}
                  >
                    {t("new-search.cost.monitoring_note")}
                  </Text>
                </Box>

                <FormControl
                  fullWidth
                  variant="standard"
                  sx={{ marginBottom: "26px" }}
                >
                  <InputLabel
                    id="type-label"
                    sx={{ fontSize: "14px !important", fontWeight: "700" }}
                  >
                    {t("new-search.fields.sorted_by")}
                  </InputLabel>
                  <Select
                    labelId="type-label"
                    id="type-label"
                    value={sortedBy}
                    onChange={(event) =>
                      setSortedBy(
                        event.target.value as "relevance" | "date" | "rating"
                      )
                    }
                    MenuProps={MenuProps}
                    color="secondary"
                  >
                    {["relevance", "date", "rating"].map((sort) => {
                      return (
                        <MenuItem key={sort} value={sort}>
                          <ListItemText primary={t("sort." + sort)} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  variant="standard"
                  sx={{ marginBottom: "26px" }}
                >
                  <InputLabel
                    id="type-label"
                    sx={{ fontSize: "14px !important", fontWeight: "700" }}
                  >
                    {t("new-search.fields.country")}
                  </InputLabel>
                  <Select
                    labelId="type-label"
                    id="type-label"
                    value={country}
                    onChange={(event) => setCountry(event.target.value)}
                    MenuProps={MenuProps}
                    color="secondary"
                  >
                    {contryList.map((c) => {
                      return (
                        <MenuItem key={c} value={c}>
                          <ListItemText primary={t("country." + c)} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  variant="standard"
                  sx={{ marginBottom: "26px" }}
                >
                  <InputLabel
                    id="type-label"
                    sx={{ fontSize: "14px !important", fontWeight: "700" }}
                  >
                    {t("new-search.fields.language")}
                  </InputLabel>
                  <Select
                    labelId="type-label"
                    id="type-label"
                    value={language}
                    onChange={(event) => setLanguage(event.target.value)}
                    MenuProps={MenuProps}
                    color="secondary"
                  >
                    {languageList.map((l) => {
                      return (
                        <MenuItem key={l} value={l}>
                          <ListItemText primary={t("language." + l)} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ marginBottom: "26px" }}>
                  <TextField
                    variant="standard"
                    label={t("new-search.fields.name")}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                    }}
                    InputLabelProps={{
                      style: {
                        width: "inherit",
                        fontSize: "14px !important",
                        fontWeight: "700",
                      },
                    }}
                    fullWidth
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    label={t("new-search.fields.blacklist")}
                    value={param}
                    onChange={(e) => setParam(e.target.value)}
                    fullWidth
                    variant="standard"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddParam();
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        width: "inherit",
                        fontSize: "14px !important",
                        fontWeight: "700",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => handleAddParam()}
                            onDoubleClick={() => handleAddParam()}
                            aria-label="Add"
                          >
                            <AddIcon color="info" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                <Grid container mt={"20px"} spacing={3}>
                  {blacklistParams.map((param, index) => (
                    <Grid key={index} item xs={3}>
                      <Paper
                        sx={{
                          backgroundColor: customOrange[50],
                          borderStyle: "solid",
                          borderColor: `${theme.palette.primary.main}`,
                          borderWidth: "1px",
                          padding: "5px 10px",
                          borderRadius: "5px",
                        }}
                      >
                        <Grid container>
                          <Grid item xs={10}>
                            <Text
                              fontSize={"13px"}
                              fontWeight={"700"}
                              sx={{
                                color: "#1D2C54",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {param}
                            </Text>
                          </Grid>

                          <Grid item xs={2}>
                            <IconButton
                              onClick={() => removeParam(param)}
                              style={{
                                padding: 0,
                                width: "20px",
                                height: "20px",
                              }}
                            >
                              <CloseRoundedIcon
                                sx={{ color: "#1D2C54", width: "16px" }}
                              />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </>
          )}

          {activeStep === 1 && (
            <Grid item xs={12}>
              {/* Pannello di stato "ricerca in corso / completata" */}
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: customOrange[50],
                  border: `1px solid ${
                    isDone ? theme.palette.primary.main : customOrange[200]
                  }`,
                  borderRadius: "12px",
                  padding: "20px 24px",
                  marginBottom: "24px",
                }}
              >
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  flexWrap={"wrap"}
                  gap={1}
                >
                  <Box>
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                      {isDone ? (
                        <CheckCircleRoundedIcon
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: 22,
                          }}
                        />
                      ) : (
                        <TravelExploreRoundedIcon
                          sx={{ color: customOrange[1000], fontSize: 22 }}
                        />
                      )}
                      <Text
                        variant="h4"
                        fontSize={"20px"}
                        fontWeight={"800"}
                        sx={{ color: "#1D2C54" }}
                      >
                        {isDone
                          ? t("new-search.results.done_title")
                          : t("new-search.results.progress_title")}
                      </Text>
                    </Box>
                    <Text
                      fontSize={"14px"}
                      fontWeight={"500"}
                      sx={{ color: "#5A6478", marginTop: "4px" }}
                    >
                      {isDone
                        ? t("new-search.results.done_count", {
                            count: liveResults.length,
                          })
                        : t("new-search.results.progress_subtitle")}
                    </Text>
                  </Box>

                  {/* Contatore live dei risultati trovati finora */}
                  <Box
                    sx={{
                      backgroundColor: "#FFFFFF",
                      border: `1px solid ${
                        isDone
                          ? theme.palette.primary.main
                          : customOrange[300]
                      }`,
                      borderRadius: "999px",
                      padding: "6px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Text
                      fontSize={"15px"}
                      fontWeight={"800"}
                      sx={{ color: customOrange[1000] }}
                    >
                      {liveResults.length}
                    </Text>
                    <Text
                      fontSize={"13px"}
                      fontWeight={"600"}
                      sx={{ color: "#1D2C54" }}
                    >
                      {liveResults.length === 0
                        ? t("new-search.results.progress_count_zero")
                        : t("new-search.results.progress_count", {
                            count: liveResults.length,
                          })}
                    </Text>
                  </Box>
                </Box>

                {/* Barra di avanzamento: indeterminata finche' non e' done */}
                {!isDone && (
                  <LinearProgress
                    color="primary"
                    variant="indeterminate"
                    sx={{
                      height: "6px",
                      width: "100%",
                      borderRadius: "999px",
                      backgroundColor: customOrange[100],
                      marginTop: "16px",
                    }}
                  />
                )}
              </Paper>

              {/* Modalita' Social (additiva): avviso non bloccante se una o piu'
                  piattaforme hanno esaurito la quota disponibile lato backend/SFM. */}
              {!!liveSearch?.quota_exceeded_platforms?.length && (
                <MuiAlert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
                  {t("new-search.results.quota_exceeded", {
                    platforms: liveSearch!.quota_exceeded_platforms!.join(", "),
                  })}
                </MuiAlert>
              )}

              {/* Tabella che si popola progressivamente con i risultati parziali.
                  Mostrata appena c'e' almeno un risultato; i record senza
                  sentiment vengono gestiti dalla SearchTable con fallback. */}
              {liveSearch && liveResults.length > 0 && (
                <SearchTable
                  search={{ ...liveSearch, results: liveResults }}
                  style={{ maxHeight: "400px" }}
                  generatePractice={handleGeneratePractice}
                  isPracticeLoading={isPracticeLoading}
                />
              )}
            </Grid>
          )}
        </Grid>
        <Box
          width={"100%"}
          display={"flex"}
          justifyContent={"flex-end"}
          marginTop={"26px"}
          maxHeight={"47px"}
        >
          <Button
            onClick={() => {
              if (activeStep === 0) {
                handleSubmit();
              } else {
                resetFields();
                navigate("/web-rep/dashboard");
                onClose();
                resetSearch();
              }
            }}
            disabled={activeStep === 0 && isSocialWithoutPlatforms}
            sx={{ mt: 1, mr: 1, padding: "0px 24px" }}
            color="primary"
            variant="contained"
          >
            {activeStep === 0 ? t("new-search.search") : t("new-search.home")}
          </Button>
        </Box>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={() => {
          setOpenSnackbar(false);
          resetErrorPractice();
          resetSuccessPractice();
        }}
      >
        <MuiAlert
          onClose={() => {
            setOpenSnackbar(false);
            resetErrorPractice();
            resetSuccessPractice();
          }}
          severity={errorPractice ? "error" : "success"}
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {errorPractice
            ? errorPractice
            : t("report.generate_practice_success")}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    search: searchSelectors.getSearch(state),
    resultDetail: searchSelectors.getResultDetail(state),
    isSearchLoading: searchSelectors.isSearchLoading(state),
    isPracticeLoading: practiceSelectors.isPracticeLoading(state),
    errorPractice: practiceSelectors.getPracticeError(state),
    successPractice: practiceSelectors.getPracticeSuccess(state),
  }),
  (dispatch: Dispatch) => ({
    createNewSearch: (payload: SearchPost) =>
      dispatch(searchAsync.newSearch(payload)),
    resetSearch: () => dispatch(searchActions.resetSearch()),
    pollDetail: (id: string) => dispatch(searchAsync.pollSearchDetail(id)),
    resetDetail: () => dispatch(searchAsync.resetDetail()),
    createPractice: (urls: Result[]) =>
      dispatch(practiceAsync.newPractice(urls)),
    resetErrorPractice: () => dispatch(practiceActions.error({ error: "" })),
    resetSuccessPractice: () =>
      dispatch(practiceActions.success({ success: "" })),
  })
)(ResearchFieldsContainer);
